import styled, { keyframes, css } from "styled-components";

const slideIn = keyframes`
  from { transform: translateX(100%) scale(0.92); opacity: 0; }
  to   { transform: translateX(0)   scale(1);    opacity: 1; }
`;

const shrink = keyframes`
  from { transform: scaleX(1); }
  to   { transform: scaleX(0); }
`;

export const Viewport = styled.div`
  position: fixed;
  top: ${({ theme }) => theme.spacing(2)};
  right: ${({ theme }) => theme.spacing(2)};
  z-index: ${({ theme }) => theme.zIndex.toast};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  max-width: 360px;
  width: calc(100vw - ${({ theme }) => theme.spacing(4)});
  pointer-events: none;

  @media (max-width: 480px) {
    top: auto;
    bottom: calc(env(safe-area-inset-bottom) + ${({ theme }) => theme.spacing(2)});
    right: ${({ theme }) => theme.spacing(1)};
    left: ${({ theme }) => theme.spacing(1)};
    width: auto;
    max-width: none;
  }
`;

export const ToastRoot = styled.div<{ $type: string }>`
  pointer-events: all;
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(1.5)};
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(2)}`};
  border-radius: ${({ theme }) => theme.radius.lg}px;
  background: ${({ theme }) => theme.colors.background.liquidGlass};
  backdrop-filter: blur(32px) saturate(180%) brightness(1.05);
  -webkit-backdrop-filter: blur(32px) saturate(180%) brightness(1.05);
  border: 1px solid ${({ theme }) => theme.colors.background.liquidGlassBorder};
  box-shadow: ${({ theme }) => theme.colors.background.liquidGlassHighlight},
    ${({ theme }) => theme.colors.shadow.high};
  animation: ${slideIn} ${({ theme }) => theme.motion.base} cubic-bezier(0.22, 1, 0.36, 1) both;
  overflow: hidden;
  min-width: 0;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  ${({ $type, theme }) =>
    $type === "success" &&
    css`
      border-left: 3px solid ${theme.colors.accent.success};
    `}
  ${({ $type, theme }) =>
    $type === "error" &&
    css`
      border-left: 3px solid ${theme.colors.accent.danger};
    `}
  ${({ $type, theme }) =>
    $type === "warning" &&
    css`
      border-left: 3px solid ${theme.colors.accent.warning};
    `}
  ${({ $type, theme }) =>
    $type === "info" &&
    css`
      border-left: 3px solid ${theme.colors.accent.info};
    `}
`;

export const IconWrap = styled.span<{ $type: string }>`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  margin-top: 1px;

  ${({ $type, theme }) =>
    $type === "success" &&
    css`
      background: ${theme.colors.accent.success}22;
      color: ${theme.colors.accent.success};
    `}
  ${({ $type, theme }) =>
    $type === "error" &&
    css`
      background: ${theme.colors.accent.danger}22;
      color: ${theme.colors.accent.danger};
    `}
  ${({ $type, theme }) =>
    $type === "warning" &&
    css`
      background: ${theme.colors.accent.warning}22;
      color: ${theme.colors.accent.warning};
    `}
  ${({ $type, theme }) =>
    $type === "info" &&
    css`
      background: ${theme.colors.accent.info}22;
      color: ${theme.colors.accent.info};
    `}
`;

export const Body = styled.div`
  flex: 1;
  min-width: 0;
`;

export const Message = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.small};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.5;
  word-break: break-word;
`;

export const ActionBtn = styled.button`
  margin-top: ${({ theme }) => theme.spacing(0.5)};
  background: none;
  border: none;
  padding: 0;
  font-size: ${({ theme }) => theme.typography.small};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  color: ${({ theme }) => theme.colors.accent.primary};
  cursor: pointer;
  text-decoration: underline;
  font-weight: 600;

  &:hover { color: ${({ theme }) => theme.colors.accent.primaryHover}; }
`;

export const DismissBtn = styled.button`
  flex-shrink: 0;
  background: none;
  border: none;
  padding: 2px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.secondary};
  border-radius: ${({ theme }) => theme.radius.xs}px;
  line-height: 1;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover { color: ${({ theme }) => theme.colors.text.primary}; background: ${({ theme }) => theme.colors.state.hover}; }
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.colors.border.focus}; outline-offset: 1px; }
`;

export const ProgressBar = styled.div<{ $duration: number }>`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 2px;
  width: 100%;
  background: ${({ theme }) => theme.colors.accent.primary}33;
  transform-origin: left;
  animation: ${shrink} ${({ $duration }) => $duration}ms linear forwards;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;
